<template>
  <v-container class="mt-4">
    <v-row v-resize="onResize">
      <v-col :cols="isMobile ? 12 : 10">
        <h1 class="mb-3" :class="{ 'text-h6': isMobile, 'text-h5': !isMobile }" v-html="$t('creativeReport.title')"></h1>
      </v-col>
      <v-col class="text-right" :cols="isMobile ? 12 : 2">
        <v-btn color="light-blue darken-1 text-white" @click="clickCampaignPeriod()">{{ $t('creativeReport.buttonCampaignPeriod') }}</v-btn>
      </v-col>
      <v-col cols="12" v-if="errorMessage">
        <v-alert :value="true" type="error" v-html="$t(errorMessage)"></v-alert>
      </v-col>
      <v-col cols="12" md="3">
        <div>
          <span class="v-label v-label--active theme--light" style="left: 0px; right: auto; font-size: 12px;">
            {{ $t('commons.filter.calendar.title.aggregateDateRange') }}
          </span>
          <br />
          <VueDatePicker
            ref="picker"
            :locale="datepickerLocaleString"
            format="yyyy/MM/dd"
            v-model="dateRange"
            :range="true"
            :ranges="ranges"
            :enable-time-picker="false"
            :min-date="minDate"
            :max-date="maxDate"
            :auto-apply="false"
            :hide-input-icon="true"
            :clearable="false"
            @update:modelValue="handlePickerUpdate"
            @closed="handlePickerClosed"
            :multi-calendars="{ solo: true, count: 2, static: false }"
            close-on-auto-apply="false"
            :month-change-on-scroll="false"
          >
            <template #trigger>
              <div class="custom-input-trigger">
                <div v-if="dateRange && dateRange.length === 2">
                  {{ formatDisplayDate(dateRange) }}
                </div>
                <div v-else style="width:188px;">
                  {{ $t('commons.filter.calendar.placeHolder') }}
                </div>
              </div>
            </template>
            <template #left-sidebar="{ }">
              <div class="dp__sidebar" style="width: 130px;">
                <ul>
                  <li v-for="(range, name) in ranges" :key="name" @click="dateRange = range" class="dp__menu_item">
                    <b>{{ name }}</b>
                  </li>
                </ul>
              </div>
            </template>
            <template #action-row="{ internalModelValue, actions }">
              <div class="slot" style="display: flex; justify-content: flex-end; gap: 5px;">
                <v-btn
                  color="light-green-darken-1 text-white"
                  @click="selectPicker()"
                  :disabled="!internalModelValue || !internalModelValue[0] || !internalModelValue[1]"
                >{{ $t('commons.global.button.select') }}
                </v-btn>
                <v-btn color="light text-black" @click="closePicker()">{{ $t('commons.global.button.cancel') }}</v-btn>
              </div>
            </template>
          </VueDatePicker>
        </div>
      </v-col>
      <v-col md="9" cols="12">
        <MultiSelect
          v-model:select-items="campaignIdsComputed"
          :items="campaignList"
          item-title="campaignName"
          item-value="campaignId"
          label="キャンペーン (複数選択可)"
          :noDataText="$t('commons.form.select.notfound')"
          :rules="[campaignIdsComputed.length !== 0 || '選択してください']"
          :required="true"
          variant="underlined"
        />
      </v-col>
      <v-col md="3" cols="6">
        <MultiSelect
          v-model:select-items="creativeTypeIdsComputed"
          :items="creativeTypeList"
          item-title="creativeTypeName"
          item-value="creativeTypeId"
          label="クリエイティブ種別 (複数選択可)"
          :noDataText="$t('commons.form.select.notfound')"
          :rules="[creativeTypeIdsComputed.length !== 0 || '選択してください']"
          :required="true"
          variant="underlined"
        />
      </v-col>
      <v-col md="2" cols="6">
        <MultiSelect
          v-model:select-items="creativeSizeIdsComputed"
          :items="creativeSizeList"
          item-title="creativeSizeName"
          item-value="creativeSizeId"
          label="サイズ (複数選択可)"
          :noDataText="$t('commons.form.select.notfound')"
          :rules="[creativeSizeIdsComputed.length !== 0 || '選択してください']"
          :required="true"
          variant="underlined"
        />
      </v-col>
      <v-col md="6" cols="12">
        <v-text-field v-model="creativeName" label="クリエイティブ名" maxlength="100" variant="underlined"></v-text-field>
      </v-col>
      <v-col md="1" cols="12" class="text-right">
        <v-btn color="blue-grey darken-4 text-white" @click="search()" :disabled="isDisabledBtn">{{ $t('commons.global.button.search') }}</v-btn>
      </v-col>
    </v-row>
    <v-row justify-center>
      <v-col cols="12">
        <div class="mb-2" :class="{ title: !isMobile, subheading: isMobile }">{{ $t('creativeReport.table.totalTitle') }}</div>
        <v-data-table
          :headers="totalHeaders"
          :items="total"
          :hide-default-header="isMobile"
          hide-default-footer
          :noDataText="$t('commons.table.no.data.text')"
          class="elevation-3 total-list"
          :class="{ mobile: isMobile }"
          :loading="isSearching"
        >
          <template v-slot:item="props">
            <tr v-if="!isMobile">
              <td class="text-right break-word-cell">{{ number_format(props.item.imp) }}</td>
              <td class="text-right break-word-cell">{{ number_format(props.item.click) }}</td>
              <td class="text-right break-word-cell">{{ percent_format(props.item.ctr) }}</td>
              <td class="text-right break-word-cell">{{ number_format(props.item.cv) }}</td>
              <td class="text-right break-word-cell">{{ percent_format(props.item.cvr) }}</td>
              <td class="text-right break-word-cell">{{ number_format_yen(props.item.digestiveAmount) }}</td>
            </tr>
            <tr v-else>
              <td>
                <ul class="flex-content">
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.imp')">{{ number_format(props.item.imp) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.click')">{{ number_format(props.item.click) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.ctr')">{{ percent_format(props.item.ctr) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.cv')">{{ number_format(props.item.cv) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.cvr')">{{ percent_format(props.item.cvr) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.digestiveAmount')">
                    {{ number_format_yen(props.item.digestiveAmount) }}
                  </li>
                </ul>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row justify-left class="mt-3">
      <v-col cols="12" class="text-right">
        <v-btn color="light-green darken-1 text-white" :disabled="isDisabledCsvDownloadBtn" @click="clickDownload()">
          <v-icon>mdi-download</v-icon>
          {{ $t('creativeReport.table.download') }}
        </v-btn>
        <div class="caption text-red-accent-4">{{ $t('creativeReport.table.infoMessage') }}</div>
      </v-col>
    </v-row>
    <v-row justify-center>
      <v-col cols="12">
        <v-data-table-server
          :headers="reportHeaders"
          :items="report"
          :hide-default-header="isMobile"
          hide-default-footer
          :no-data-text="$t('commons.table.no.data.text')"
          class="elevation-3 report-list"
          :class="{ mobile: isMobile }"
          v-model:sort-by="pagination.sortBy"
          :items-per-page="pagination.rowsPerPage"
          :items-length="totalCreative"
          :loading="isSearching"
          @update:options="handleTableOptionsUpdate"
        >
          <template v-slot:item="props">
            <tr v-if="!isMobile">
              <td class="wrap-1 text-left break-word-cell">{{ props.item.creativeName }}</td>
              <td v-if="props.item.creativeType == 8" class="text-center">
                <img
                  v-bind:src="props.item.thumbnail"
                  style="margin-top: 3px; max-width: 120px; max-height: 90px"
                  v-on:click="previewDialog(props.item.consoleCreativeId, props.item.preview, props.item.creativeSize)"
                />
                <br />
                <v-btn
                  style="height: 25px; text-transform: capitalize"
                  color="light-gray darken-1 black--text"
                  v-on:click="previewDialog(props.item.consoleCreativeId, props.item.preview, props.item.creativeSize)"
                  >Preview</v-btn
                >
              </td>
              <td v-else-if="props.item.creativeType == 10" class="text-center">
                <img
                  v-bind:src="props.item.thumbnail"
                  v-on:click="previewCkickOpenWindow(props.item.consoleCreativeId, props.item.preview)"
                  style="margin-top: 3px; max-width: 120px; max-height: 90px"
                />
                <br />
                <v-btn
                  style="height: 25px; text-transform: capitalize"
                  color="light-gray darken-1 black--text"
                  v-on:click="previewCkickOpenWindow(props.item.consoleCreativeId, props.item.preview)"
                >
                  <v-icon style="height: 20px;font-size:15px;">mdi-open-in-new</v-icon>
                  Play
                </v-btn>
              </td>
              <td v-else-if="props.item.creativeType == 4" class="text-center">
                <img
                  v-on:click="previewDialog(props.item.consoleCreativeId, convertDynamicCreative(props.item.preview), props.item.creativeSize)"
                  v-bind:src="props.item.thumbnail"
                  style="margin-top: 3px; max-width: 120px; max-height: 90px"
                />
                <br />
                <v-btn
                  style="height: 25px; text-transform: capitalize"
                  color="light-gray darken-1 black--text"
                  v-on:click="previewDialog(props.item.consoleCreativeId, convertDynamicCreative(props.item.preview), props.item.creativeSize)"
                  >Preview</v-btn
                >
              </td>
              <td v-else-if="props.item.creativeType == 5 || props.item.creativeType == 6" class="text-center">
                <img
                  v-on:click="
                    previewDialog(props.item.consoleCreativeId, props.item.preview, '300x350', $t('submissionConfirmationCreativeSetting.dialog.notes'))
                  "
                  v-bind:src="props.item.thumbnail"
                  style="margin-top: 3px; max-width: 120px; max-height: 90px"
                />
                <br />
                <v-btn
                  style="height: 25px; text-transform: capitalize"
                  color="light-gray darken-1 black--text"
                  v-on:click="
                    previewDialog(props.item.consoleCreativeId, props.item.preview, '300x350', $t('submissionConfirmationCreativeSetting.dialog.notes'))
                  "
                  >Preview</v-btn
                >
              </td>
              <td v-else-if="props.item.creativeType == 1" class="text-center">
                <img
                  v-on:click="previewResizeDialog(props.item.consoleCreativeId, props.item.preview, props.item.creativeSize)"
                  v-bind:src="props.item.thumbnail"
                  style="margin-top: 3px; max-width: 120px; max-height: 90px"
                />
                <br />
                <v-btn
                  style="height: 25px; text-transform: capitalize"
                  color="light-gray darken-1 black--text"
                  v-on:click="previewResizeDialog(props.item.consoleCreativeId, props.item.preview, props.item.creativeSize)"
                  >Preview</v-btn
                >
              </td>
              <td v-else class="text-center">
                <img
                  v-on:click="previewDialog(props.item.consoleCreativeId, props.item.preview, props.item.creativeSize)"
                  v-bind:src="props.item.thumbnail"
                  style="margin-top: 3px; max-width: 120px; max-height: 90px"
                />
                <br />
                <v-btn
                  style="height: 25px; text-transform: capitalize"
                  color="light-gray darken-1 black--text"
                  v-on:click="previewDialog(props.item.consoleCreativeId, props.item.preview, props.item.creativeSize)"
                  >Preview</v-btn
                >
              </td>

              <td class="wrap-2 text-right break-word-cell">{{ number_format(props.item.imp) }}</td>
              <td class="wrap-2 text-right break-word-cell">{{ number_format(props.item.click) }}</td>
              <td class="wrap-2 text-right break-word-cell">{{ percent_format(props.item.ctr) }}</td>
              <td class="wrap-2 text-right break-word-cell">{{ number_format(props.item.cv) }}</td>
              <td class="wrap-2 text-right break-word-cell">{{ percent_format(props.item.cvr) }}</td>
              <td class="wrap-2 text-right break-word-cell">{{ number_format_yen(props.item.digestiveAmount) }}</td>
              <td class="wrap-2 text-center break-word-cell">{{ props.item.creativeSize }}</td>
              <td class="wrap-2 text-center break-word-cell">{{ props.item.creativeTypeName }}</td>
            </tr>
            <tr v-else>
              <td>
                <ul class="flex-content">
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.creativeName')">{{ props.item.creativeName }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.thumbnail')">
                    <img v-bind:src="props.item.thumbnail" style="margin-top: 3px; max-width: 120px; max-height: 90px" />
                  </li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.imp')">{{ number_format(props.item.imp) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.click')">{{ number_format(props.item.click) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.ctr')">{{ percent_format(props.item.ctr) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.cv')">{{ number_format(props.item.cv) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.cvr')">{{ percent_format(props.item.cvr) }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.digestiveAmount')">
                    {{ number_format_yen(props.item.digestiveAmount) }}
                  </li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.creativeSize')">{{ props.item.creativeSize }}</li>
                  <li class="flex-item word-break" :data-label="$t('creativeReport.table.headers.creativeType')">{{ props.item.creativeTypeName }}</li>
                </ul>
              </td>
            </tr>
          </template>
        </v-data-table-server>
      </v-col>
      <!-- ダイアログ -->
      <div class="preview-pos" v-show="hoverDialogFlag">
        <v-dialog v-model="dialog" :max-width="`${dialogWidth}`">
          <v-card>
            <v-card-actions>
              <div v-html="`${previewImage}`"></div>
            </v-card-actions>
            <v-card-text v-if="description !== ''">
              <span v-html="`${description}`"></span>
            </v-card-text>
            <v-col xs12 class="text-right">
              <v-btn class="align-self-auto" color="green darken-1" text @click.native="dialog = false">Close</v-btn>
            </v-col>
          </v-card>
        </v-dialog>
      </div>
    </v-row>
    <div class="text-center">
      <v-pagination v-model="page" :length="totalPage" :total-visible="10" @update:modelValue="pageChange" :disabled="isDisabledBtn" active-color="blue-darken-2"></v-pagination>
    </div>
    <v-dialog v-model="warnDlg.isOpen" persistent :max-width="warnDlg.maxWidth">
      <v-card justify="center">
        <v-card-title>
          <v-icon>mdi-alert</v-icon>
          {{ warnDlg.headline }}
        </v-card-title>
        <v-card-text>
          <div style="margin-left: 10px">{{ warnDlg.msg }}</div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="darken-1" text @click="warnDlg.isOpen = false">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import MultiSelect from './ui/MultiSelect'
import { useFormatters } from '../plugins/format';

export default {
  components: { VueDatePicker, MultiSelect },
  name: 'CreativeReportList',
  props: ['agencyId', 'advertiserId'],
  setup() {
    const {
      number_format,
      number_format_yen,
      percent_format,
    } = useFormatters();
    return {
      number_format,
      number_format_yen,
      percent_format,
    };
  },
  data: function () {
    return {
      pagination: {
        // 2重クリック防止用
        isSorting: false,
        sortBy: [{ key: 'creativeType', order:'asc' }],
        descending: false,
        rowsPerPage: -1,
        isInitialized: false
      },
      totalCreative: 0,
      // フィルタ：INPUT
      campaignIds: [],
      creativeSizeIds: [],
      creativeTypeIds: [],
      creativeName: '',
      dateRange: [],
      // 初期 or 検索ボタンを押した状態を保持する( DL やページングはこの条件を用いる)
      commiteCampaignIds: [],
      commiteCreativeSizeIds: [],
      commiteCreativeTypeIds: [],
      commiteCreativeName: '',
      commitedateRange: [],
      // 画面描画条件
      opens: 'right',
      ranges: {},
      minDate: null,
      maxDate: null,
      campaignList: [],
      creativeTypeList: [],
      creativeSizeList: [],
      isCampaignAllSelect: false,
      isCreativeTypeAllSelect: false,
      isCreativeSizeAllSelect: false,
      isSearching: false,
      isCsvDownload: false,
      totalHeaders: [
        {
          title: this.$t('creativeReport.table.headers.imp'),
          align: 'center',
          key: 'imp',
          sortable: false
        },
        {
          title: this.$t('creativeReport.table.headers.click'),
          align: 'center',
          key: 'click',
          sortable: false
        },
        {
          title: this.$t('creativeReport.table.headers.ctr'),
          align: 'center',
          key: 'ctr',
          sortable: false
        },
        {
          title: this.$t('creativeReport.table.headers.cv'),
          align: 'center',
          key: 'cv',
          sortable: false
        },
        {
          title: this.$t('creativeReport.table.headers.cvr'),
          align: 'center',
          key: 'cvr',
          sortable: false
        },
        {
          title: this.$t('creativeReport.table.headers.digestiveAmount'),
          align: 'center',
          key: 'digestiveAmount',
          sortable: false
        }
      ],
      total: [],
      reportHeaders: [
        {
          title: this.$t('creativeReport.table.headers.creativeName'),
          align: 'center',
          key: 'creativeName',
          sortable: true
        },
        {
          title: this.$t('creativeReport.table.headers.thumbnail'),
          align: 'center',
          key: 'thumbnail',
          sortable: false
        },
        {
          title: this.$t('creativeReport.table.headers.brimp'),
          align: 'center',
          key: 'imp',
          sortable: true
        },
        {
          title: this.$t('creativeReport.table.headers.brclick'),
          align: 'center',
          key: 'click',
          sortable: true
        },
        {
          title: this.$t('creativeReport.table.headers.ctr'),
          align: 'center',
          key: 'ctr',
          sortable: true
        },
        {
          title: this.$t('creativeReport.table.headers.brcv'),
          align: 'center',
          key: 'cv',
          sortable: true
        },
        {
          title: this.$t('creativeReport.table.headers.cvr'),
          align: 'center',
          key: 'cvr',
          sortable: true
        },
        {
          title: this.$t('creativeReport.table.headers.digestiveAmount'),
          align: 'center',
          key: 'digestiveAmount',
          sortable: true
        },
        {
          title: this.$t('creativeReport.table.headers.creativeSize'),
          align: 'center',
          key: 'creativeSize',
          sortable: false
        },
        {
          title: this.$t('creativeReport.table.headers.creativeType'),
          align: 'center',
          key: 'creativeType',
          sortable: true
        }
      ],
      report: [],
      page: undefined, // 現在の表示ページ
      tmpPage: undefined,
      perPage: 100, // 1ページの表示件数
      defaultPerPage: 100,
      totalPage: undefined, // トータルページ数
      count: 0, // 総件数
      isMobile: false,
      errorMessage: undefined,
      warnDlg: {
        'text-h5': '',
        msg: '',
        maxWidth: 300,
        isOpen: false
      },
      preview: '',
      // hoverFlag: false,
      hoverDialogFlag: false,
      hoverVideoFlag: false,
      hoverIndex: null,
      previewImage: '',
      previewMovie: '',
      description: '',
      dialog: false,
      dialogWidth: 0,
      image_more_src: require(`../../public/imgs/outline_search_black_18dp.png`),
      picker: null,
      todayDate: null
    }
  },
  methods: {
    ...mapActions('creativeReport', ['getFilterList', 'getCreativeReport', 'getCsvDownload']),
    ...mapActions('headerParam', ['setHeaderParamAgencyAdvertiser']),

    handlePickerUpdate: function (newDate) {
      if (newDate && newDate.length === 2) {
        this.dateRange = newDate;
        this.commitedateRange = [...newDate];
        this.page = 1;
      }
    },
    handlePickerClosed: function () {
      if (this.dateRange[0]?.getTime() !== this.commitedateRange[0]?.getTime() ||
          this.dateRange[1]?.getTime() !== this.commitedateRange[1]?.getTime()) {
        this.dateRange = [...this.commitedateRange];
      }
    },

    selectPicker: function () {
      if (this.$refs.picker) {
        this.$refs.picker.selectDate(); // VueDatePickerの内部メソッドを呼び出す
        this.$refs.picker.closeMenu(); // VueDatePickerの内部メソッドを呼び出す
        this.commitedateRange = [...this.dateRange]; // 現在の選択を保存
      }
    },

    closePicker: function () {
      this.dateRange = [this.startDate]; // 保存しておいた選択に戻す
      this.$refs.picker.closeMenu(); // VueDatePickerの内部メソッドを呼び出す
    },

    initialize: function () {
      this.initializeDateTimeRanges()

      this.initializeFilter().then((flg) => {
        if (flg) {
          this.isInitialized = true;
          this.previewCreativeReportList()
        }
      })
      this.setHeaderParamAgencyAdvertiser({ agencyId: this.agencyId, advertiserId: this.advertiserId })
    },
    initializeDateTimeRanges: function () {
      // 今日
      const today = moment().startOf('day').toDate()
      // 昨日
      const yesterday = moment(today).add(-1, 'days').startOf('day').toDate()
      // 当日から一週間前
      const lastWeekAgo = moment(today).add(-6, 'days').toDate()
      // 昨日から一週間前
      const lastWeekDayAgo = moment(yesterday).add(-6, 'days').toDate()
      // 月初
      const monthFirst = moment(today).startOf('month').toDate()
      // 先月末
      const lastMonthEnd = moment(today).startOf('month').add(-1, 'days').toDate()
      // 先月月初
      const lastMonthFirst = moment(lastMonthEnd).startOf('month').toDate()
      // 30日前
      const day30ago = moment(today).add(-29, 'days').toDate()
      // 半年前（厳密な半年ではないが、代理店画面と合わせた）
      const halfYearDay = moment(today).add(-6, 'month').add(1, 'days').toDate()
      this.ranges = {
        [this.$t('commons.filter.calendar.today')]: [today, today],
        [this.$t('commons.filter.calendar.yesterday')]: [yesterday, yesterday],
        [this.$t('commons.filter.calendar.lastWeekAgo')]: [lastWeekAgo, today],
        [this.$t('commons.filter.calendar.lastWeekDayAgo')]: [lastWeekDayAgo, yesterday],
        [this.$t('commons.filter.calendar.day30ago')]: [day30ago, today],
        [this.$t('commons.filter.calendar.halfYearDay')]: [halfYearDay, today],
        [this.$t('commons.filter.calendar.thisMonth')]: [monthFirst, today],
        [this.$t('commons.filter.calendar.thisMonthYesterday')]: [monthFirst, yesterday],
        [this.$t('commons.filter.calendar.lastMonth')]: [lastMonthFirst, lastMonthEnd]
      }
      this.maxDate = today
      // 代理店画面と合わせて半年前の1日まで選択可能とした
      this.minDate = moment(halfYearDay).startOf('month').toDate()
      // 初期選択（引数がなければ当月とする）
      let startDate = this.$route.query.from ? moment(this.$route.query.from, 'YYYYMMDD').toDate() : monthFirst
      let endDate = this.$route.query.to ? moment(this.$route.query.to, 'YYYYMMDD').toDate() : today
      this.dateRange = [ startDate, endDate ]
      this.commitedateRange = [ startDate, endDate ]
    },
    createUrlParameter: function () {
      var url = location.href
      if (url.indexOf('?') !== -1) {
        url = url.substring(0, url.indexOf('?'))
      }
      let param = []

      // campaignId
      if (Object.keys(this.campaignList).length !== this.commiteCampaignIds.length && this.commiteCampaignIds.length > 0) {
        param.push(`cIds=${this.commiteCampaignIds.join(',')}`)
      }
      // creativeSizeId
      if (Object.keys(this.creativeSizeList).length !== this.commiteCreativeSizeIds.length && this.commiteCreativeSizeIds.length > 0) {
        param.push(`sIds=${this.commiteCreativeSizeIds.join(',')}`)
      }
      // creativeTypeId
      if (Object.keys(this.creativeTypeList).length !== this.commiteCreativeTypeIds.length && this.commiteCreativeTypeIds.length > 0) {
        param.push(`tIds=${this.commiteCreativeTypeIds.join(',')}`)
      }
      // 計測期間
      if (this.dateRange) {
        param.push(`from=${this.formatParameterDate(this.commitedateRange[0])}`)
        param.push(`to=${this.formatParameterDate(this.commitedateRange[1])}`)
      }
      // クリエイティブ名
      if (this.commiteCreativeName) {
        param.push(`c=${encodeURIComponent(this.commiteCreativeName)}`)
      }
      // ページ番号
      if (this.page) {
        param.push(`p=${encodeURIComponent(this.page)}`)
      }
      // 1ページの表示件数
      if (this.perPage !== this.defaultPerPage) {
        param.push(`pc=${encodeURIComponent(this.perPage)}`)
      }
      // ソート
      if (this.pagination.sortBy && this.pagination.sortBy.length > 0) { // ★修正
        param.push(`sk=${this.pagination.sortBy[0].key}`)
        param.push(`sd=${this.pagination.sortBy[0].order === 'desc' ? 'true' : 'false'}`) // ★修正
      } else {
        // ソートがない場合はURLパラメータに含めない (APIがソートなしを認識するため)
      }

      history.replaceState(null, null, url + '?' + param.join('&'))
    },
    // フィルター部分の初期化処理
    initializeFilter: async function () {
      const jwtToken = this.$store.getters['auth/jwtToken']
      const agencyId = this.agencyId
      const advertiserId = this.advertiserId
      let flg = false

      // 後続処理でレポートを取得したいのでこの処理が終わるまで後続処理を待つ
      await this.getFilterList({
        privateCode: process.env.ProjectCode,
        agencyId: agencyId,
        advertiserId: advertiserId,
        jwtToken: jwtToken
      })
        .then((result) => {
          this.errorMessage = undefined
          if (!result) {
            // 表示するデータがない場合
            this.campaignList = []
            this.creativeTypeList = []
            this.creativeSizeList = []
            return flg
          }
          // Select ボックスの初期値を設定
          this.campaignList = result.campaignList
          this.creativeTypeList = result.creativeTypeList
          this.creativeSizeList = result.creativeSizeList
          // キャンペーンの選択選択状態をハンドリングする
          this.setInitializeParameter()
          // キャンペーンは選択必須のため初期選択されていなければ後続処理は行わない
          if (this.commiteCampaignIds.length > 0) {
            flg = true
          }
        })
        .catch((err) => {
          if (err instanceof ReferenceError) {
            this.errorMessage = err.message
          } else {
            // idToken が切れているのでセッションを切り、ログイン画面に遷移する
            this.$store.dispatch('auth/logout')
            this.$router.go({ path: this.$router.currentRoute.path, force: true })
          }
        })

      return flg
    },
    setInitializeParameter: function () {
      this.initializeCampaignIds()
      this.initializeCreativeTypeIds()
      this.initializeCreativeSizeIds()
      this.initializeCreativeName()
      this.initializeSortKey()
    },
    initializeCampaignIds: function () {
      let cIds = []
      if (this.campaignIdsComputed.length > 0) {
        cIds = this.campaignIdsComputed
      } else {
        cIds = this.$route.query.cIds ? String(this.$route.query.cIds).split(',') : null
      }

      if (cIds) {
        let numberIds = []
        cIds.forEach((cId) => {
          numberIds.push(Number(cId))
        })
        this.campaignIdsComputed = numberIds
        this.commiteCampaignIds = numberIds
      } else {
        let ids = []
        this.campaignList.forEach(function (item) {
          ids.push(item['campaignId'])
        })
        this.campaignIdsComputed = ids
        this.commiteCampaignIds = ids
      }
    },
    initializeCreativeTypeIds: function () {
      let tIds = []
      if (this.creativeTypeIdsComputed.length > 0) {
        tIds = this.creativeTypeIdsComputed
      } else {
        tIds = this.$route.query.tIds ? String(this.$route.query.tIds).split(',') : null
      }
      // let initSelectcampaignIds = []
      if (tIds) {
        let numberIds = []
        tIds.forEach((tId) => {
          numberIds.push(Number(tId))
        })
        this.creativeTypeIdsComputed = numberIds
        this.commiteCreativeTypeIds = numberIds
      } else {
        let ids = []
        this.creativeTypeList.forEach(function (item) {
          ids.push(item['creativeTypeId'])
        })
        this.creativeTypeIdsComputed = ids
        this.commiteCreativeTypeIds = ids
      }
    },
    initializeCreativeSizeIds: function () {
      let sIds = []
      if (this.creativeSizeIdsComputed.length > 0) {
        sIds = this.creativeSizeIdsComputed
      } else {
        sIds = this.$route.query.sIds ? String(this.$route.query.sIds).split(',') : null
      }
      if (sIds) {
        let numberIds = []
        sIds.forEach((sId) => {
          numberIds.push(Number(sId))
        })
        this.creativeSizeIdsComputed = numberIds
        this.commiteCreativeSizeIds = numberIds
      } else {
        let ids = []
        this.creativeSizeList.forEach(function (item) {
          ids.push(item['creativeSizeId'])
        })
        this.creativeSizeIdsComputed = ids
        this.commiteCreativeSizeIds = ids
      }
    },
    initializeCreativeName: function () {
      if (this.$route.query.c) {
        const cName = this.$route.query.c
        this.creativeName = cName
        this.commiteCreativeName = cName
      }
    },
    initializeSortKey: function () {
      if (this.$route.query.sk) {
        this.pagination.sortBy[0].key = this.$route.query.sk
        this.pagination.descending = this.parseStrToBoolean(this.$route.query.sd)
      } else {
        this.pagination.sortBy = []; // URLクエリでソート指定がなければ、ソートなし状態（空配列）
        this.pagination.descending = undefined
      }
    },
    parseStrToBoolean: function(str) {
      if (str === 'true') {
        return true
      } else {
        return false
      }
    },
    warnDialog: function (title, message) {
      this.warnDlg.headline = title
      this.warnDlg.msg = message
      this.warnDlg.isOpen = true
    },
    clickCampaignPeriod: function () {
      this.$router.push(`/campaignPeriodList/${this.agencyId}/${this.advertiserId}`)
    },
    // 検索ボタン押下イベント
    search: function () {
      if (this.campaignIdsComputed.length === 0 || this.creativeTypeIdsComputed.length === 0 || this.creativeSizeIdsComputed.length === 0) {
        this.warnDialog('確認', '条件が指定されていません')
        return
      }

      this.commiteCampaignIds = this.campaignIdsComputed.slice()
      this.commiteCreativeSizeIds = this.creativeSizeIdsComputed.slice()
      this.commiteCreativeTypeIds = this.creativeTypeIdsComputed.slice()
      this.commiteCreativeName = this.creativeName
      this.commitedateRange = this.dateRange
      this.page = 1
      this.previewCreativeReportList()
    },
    clickDownload: function () {
      this.isSearching = true
      this.isCsvDownload = false

      const jwtToken = this.$store.getters['auth/jwtToken']
      const agencyId = this.agencyId
      const advertiserId = this.advertiserId
      const from = this.formatParameterDate(this.commitedateRange[0])
      const to = this.formatParameterDate(this.commitedateRange[1])
      const campaignIds = this.createParameter(this.commiteCampaignIds, this.campaignList)
      const creativeTypeIds = this.createParameter(this.commiteCreativeTypeIds, this.creativeTypeList)
      const creativeSizeIds = this.createParameter(this.commiteCreativeSizeIds, this.creativeSizeList)
      const creativeName = this.commiteCreativeName
      let sortKey = null
      if (this.pagination.sortBy && this.pagination.sortBy.length > 0 && this.pagination.sortBy[0].key != undefined) {
        sortKey = this.pagination.sortBy[0].key;
      }
      
      const sortDesc = (this.pagination.sortBy && this.pagination.sortBy.length > 0)
        ? (this.pagination.sortBy[0].order === 'desc')
        : undefined;

      const charset = 'Shift_JIS'

      this.getCsvDownload({
        privateCode: process.env.ProjectCode,
        agencyId: agencyId,
        advertiserId: advertiserId,
        from: from,
        to: to,
        campaignIds: campaignIds,
        creativeTypeIds: creativeTypeIds,
        creativeSizeIds: creativeSizeIds,
        creativeName: creativeName,
        sortKey: sortKey,
        sortDesc: sortDesc,
        charset: charset,
        jwtToken: jwtToken
      })
        .then((result) => {
          this.errorMessage = undefined
        })
        .catch((err) => {
          if (err instanceof ReferenceError) {
            this.errorMessage = err.message
          } else {
            // idToken が切れているのでセッションを切り、ログイン画面に遷移する
            this.$store.dispatch('auth/logout')
            this.$router.go({ path: this.$router.currentRoute.path, force: true })
          }
        })
        .then(() => {
          this.isSearching = false
          this.isCsvDownload = true
        })
    },
    // ソートイベント
    handleTableOptionsUpdate(options) {
      this.pagination.sortBy = options.sortBy || []; 

      this.page = options.page;
      this.pagination.rowsPerPage = options.itemsPerPage;

      // データの再取得トリガー
      this.previewCreativeReportList();
    },
    createParameter(params, list) {
      if (Object.keys(list).length !== params.length) {
        return params.join(',')
      }
    },
    previewCreativeReportList: async function () {
      if (!this.isInitialized) {
        return;
      }
      this.isSearching = true
      this.isCsvDownload = false

      const jwtToken = this.$store.getters['auth/jwtToken']
      const agencyId = this.agencyId
      const advertiserId = this.advertiserId
      const from = this.formatParameterDate(this.commitedateRange[0])
      const to = this.formatParameterDate(this.commitedateRange[1])
      const campaignIds = this.createParameter(this.commiteCampaignIds, this.campaignList)
      const creativeTypeIds = this.createParameter(this.commiteCreativeTypeIds, this.creativeTypeList)
      const creativeSizeIds = this.createParameter(this.commiteCreativeSizeIds, this.creativeSizeList)
      const creativeName = this.commiteCreativeName
      const sortKey = (this.pagination.sortBy && this.pagination.sortBy.length > 0)
        ? this.pagination.sortBy[0].key
        : undefined;
      const sortDesc = (this.pagination.sortBy && this.pagination.sortBy.length > 0)
        ? (this.pagination.sortBy[0].order === 'desc')
        : undefined;
      let page = 1
      if (this.$route.query.p && this.page === undefined) {
        page = this.$route.query.p
      } else if (this.page) {
        page = this.page
      }
      let perPage = this.defaultPerPage
      if (this.$route.query.pc) {
        perPage = this.$route.query.pc
      } else if (this.perPage) {
        perPage = this.perPage
      }

      await this.getCreativeReport({
        privateCode: process.env.ProjectCode,
        agencyId: agencyId,
        advertiserId: advertiserId,
        from: from,
        to: to,
        campaignIds: campaignIds,
        creativeTypeIds: creativeTypeIds,
        creativeSizeIds: creativeSizeIds,
        creativeName: creativeName,
        sortKey: sortKey,
        sortDesc: sortDesc,
        page: page,
        perPage: perPage,
        jwtToken: jwtToken
      })
        .then((result) => {
          this.errorMessage = undefined
          if (!result) {
            // 表示するデータがない場合
            this.total = []
            this.report = []
            this.isCsvDownload = false
            return
          }
          this.total = Object.keys(result.total).length > 0 ? [result.total] : []
          this.report = result.report
          this.totalPage = result.totalPage
          this.page = Number(result.page)
          this.perPage = Number(result.perPage)
          this.count = result.report.length

          if (result.report.length > 0) {
            this.isCsvDownload = true
            this.totalCreative = result.report.length
          }
          // URLにパラメータを付与
          this.createUrlParameter()
        })
        .catch((err) => {
          if (err instanceof ReferenceError) {
            this.errorMessage = err.message
          } else {
            // idToken が切れているのでセッションを切り、ログイン画面に遷移する
            this.$store.dispatch('auth/logout')
            this.$router.go({ path: this.$router.currentRoute.path, force: true })
          }
        })
        .then(() => {
          this.isSearching = false
        })
    },
    previewDialog: function (index, previewImage, creativeSize, description) {
      this.hoverDialogFlag = true
      this.hoverIndex = index
      this.previewImage = previewImage
      this.dialog = true
      let size = creativeSize.split('x')
      let widthNumber = Number(size[0]) + 20
      this.dialogWidth = String(widthNumber)
      this.description = description !== undefined ? description : ''
    },
    previewResizeDialog: function (index, previewImage, creativeSize) {
      this.description = ''
      this.hoverDialogFlag = true
      this.hoverIndex = index
      this.previewImage = previewImage
      this.dialog = true
      let size = creativeSize.split('x')
      let w = Number(size[0])
      let h = Number(size[1])
      let maxWidth = 1024
      let maxHeight = 600
      if (w > h) {
        if (w > maxWidth) {
          h = (maxWidth * h) / w
          w = maxWidth
        }
      } else {
        if (h > maxHeight) {
          w = (maxHeight * w) / h
          h = maxHeight
        }
      }

      let widthNumber = Number(w) + 20
      this.dialogWidth = String(widthNumber)
    },
    previewCkickOpenWindow: function (index, previewMovie) {
      this.hoverVideoFlag = true
      this.hoverIndex = index
      this.previewMovie = previewMovie
      window.open(previewMovie, null)
    },
    convertDynamicCreative: function (scriptTag) {
      // Dynamic クリエイティブのプレビュータグはスクリプトタグで生成されている為、
      // プレビュー時はバックエンド側で生成したiframeタグのみを抽出する
      const previewTagStartPos = 47
      const previewTagEndPos = -12
      return scriptTag.slice(previewTagStartPos, previewTagEndPos)
    },
    onResize: function () {
      if (window.innerWidth < 960) {
        this.isMobile = true
      } else {
        this.isMobile = false
      }
    },
    formatDisplayDate(value) {
      return `${this.formatDate(value[0], 'YYYY/MM/DD')} - ${this.formatDate(value[1], 'YYYY/MM/DD')}`
    },
    formatParameterDate(value) {
      return this.formatDate(value, 'YYYYMMDD')
    },
    formatDate(value, format) {
      return moment(value).format(format)
    },
    pageChange: function (pageNumber) {
      this.page = pageNumber
      this.previewCreativeReportList()
    },
  },
  computed: {
    datepickerLocaleString() {
      return 'ja';
    },
    campaignIdsComputed: {
      get() {
        return this.campaignIds
      },
      set(value) {
        this.campaignIds = value
      }
    },
    creativeTypeIdsComputed: {
      get() {
        return this.creativeTypeIds
      },
      set(value) {
        this.creativeTypeIds = value
      }
    },
    creativeSizeIdsComputed: {
      get() {
        return this.creativeSizeIds
      },
      set(value) {
        this.creativeSizeIds = value
      }
    },
    isDisabledBtn: function () {
      if (this.isSearching) {
        return true
      }
      return false
    },
    isDisabledCsvDownloadBtn: function () {
      if (this.isDisabledBtn) {
        return true
      }
      if (!this.isCsvDownload) {
        return true
      }
      return false
    }
  },
  beforeRouteUpdate: function (to, from, next) {
    next()
  },
  
  mounted: function () {
    this.initialize()
    this.todayDate = new Date()
  }
}
</script>
<style scoped>
.calendars::after {
  content: '';
  display: block;
  clear: both;
}
.slot {
  padding: 0.5rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.wrap-1 {
  min-width: 300px !important;
}
.wrap-2 {
  min-width: 113px !important;
}
.custom-input-trigger {
  border: 1px solid #ccc;
  padding: 8px;
  cursor: pointer;
  max-width: 210px;
}

:deep(.dp__arrow_top) {left: 130px !important;}
:deep(.dp__arrow_bottom) {left: 130px important;}
</style>
